<template>
  <div>
    <!-- ページヘッダー -->
    <bs-page-title
      :titleLabel="fromParentTitle"
      :btnLabel="
        chk_relation_type_editable('/information/data') ? fromParentBtn : false
      "
      :btnClass="'btn-primary'"
      :btnToLink="
        chk_relation_type_editable('/information/data')
          ? '/information/add'
          : ''
      "
      :routerSw="true"
    />
    <bs-msg-alert :alertClass="alertClass">
      {{ alertMessage }}
    </bs-msg-alert>

    <!-- 検索セクション -->
    <section class="search-section mb-5">
      <h5>検索</h5>
      <div class="search-form">
        <!-- お知らせ種別と状態 -->
        <div class="row mb-3">
          <div class="col-6">
            <div class="row">
              <label class="col-4 text-center py-1">お知らせ種別</label>
              <div class="col-8">
                <bs-select
                  :options="information_category_type_list"
                  v-model="sprm.category_type"
                  :null-option="true"
                  :classParam="'form-select'"
                />
              </div>
            </div>
          </div>
          <div class="col-6">
            <div class="row">
              <label class="col-4 text-center py-1">状態</label>
              <div class="col-8">
                <bs-select
                  :options="information_public_type_list"
                  v-model="sprm.public_type"
                  :null-option="true"
                  :classParam="'form-select'"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- 配信先と公開状態 -->
        <div class="row mb-3">
          <div class="col-6">
            <div class="row">
              <label class="col-4 text-center py-1">配信先</label>
              <div class="col-8">
                <bs-select
                  :options="information_target_type_list"
                  v-model="sprm.target_type"
                  :null-option="true"
                  :classParam="'form-select'"
                />
              </div>
            </div>
          </div>
          <div class="col-6">
            <div class="row">
              <label class="col-4 text-center py-1">公開状態</label>
              <div class="col-8">
                <bs-select
                  :options="status_list"
                  v-model="sprm.stat"
                  :null-option="true"
                  :classParam="'form-select'"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- 件名（1列） -->
        <div class="row mb-3">
          <label class="col-2 text-center py-1">件名</label>
          <div class="col-10">
            <input
              type="text"
              class="form-control"
              v-model="sprm.subject"
              placeholder="件名で検索"
            />
          </div>
        </div>

        <!-- 検索ボタン群 -->
        <div class="row mt-4">
          <div class="col-12 text-center">
            <div class="d-flex justify-content-center gap-2">
              <bs-button
                :btnClass="'btn-primary'"
                @to_click_event="searchInformation"
              >
                条件で検索
              </bs-button>
              <bs-button
                :btnClass="'btn-light btn-outline-secondary'"
                @to_click_event="clear"
              >
                検索条件クリア
              </bs-button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 検索結果テーブル -->
    <section class="result-section">
      <div
        v-if="
          typeof information_list === 'object' &&
          Object.keys(information_list).length > 0
        "
      >
        <table class="table">
          <thead>
            <tr>
              <th style="width: 12%">日付</th>
              <th style="width: 30%">件名</th>
              <th style="width: 15%">種別</th>
              <th style="width: 15%">配信先</th>
              <th style="width: 10%">公開状態</th>
              <th style="width: 20%">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in information_list" :key="item.id">
              <td>{{ item.set_date_date_label }}</td>
              <td>{{ item.subject }}</td>
              <td>{{ item.category_type_label }}</td>
              <td>{{ item.target_type_label }}</td>
              <td>
                <span :class="getStatusClass(item.stat)">
                  {{ getTargetLabel(item.stat) }}
                </span>
              </td>
              <td>
                <div class="d-flex gap-1">
                  <bs-button
                    :btnClass="'btn-light btn-sm btn-outline-secondary'"
                    @to_click_event="handleClick(item.id)"
                  >
                    詳細
                  </bs-button>
                  <bs-button
                    :btnClass="'btn-danger btn-sm'"
                    @to_click_event="delData(item.id)"
                    v-if="chk_relation_type_editable('/information/data')"
                  >
                    公開状態切り替え
                  </bs-button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else>
        <p class="alert alert-warning">表示するデータがありません</p>
      </div>
    </section>

    <!-- ローディング表示を追加 -->
    <div class="loading-overlay" v-if="isLoading">
      <div class="spinner-border text-danger" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import BsPageTitle from '../../molecules/BsPageTitle.vue'
import BsMsgAlert from '../../atoms/BsMsgAlert.vue'
import BsButton from '../../atoms/BsButton.vue'
import BsSelect from '../../atoms/BsSelect.vue'
import config from '../../../const'

// 初期検索パラメータ
const INITIAL_SEARCH_PARAMS = {
  category_type: '',
  public_type: '',
  target_type: '',
  subject: '',
  stat: ''
}

// 公開状態の定数
const STATUS_OPTIONS = [
  { key: '9', label: '非公開' },
  { key: '1', label: '公開' }
]

export default {
  name: 'InformationListView',

  components: {
    BsPageTitle,
    BsButton,
    BsMsgAlert,
    BsSelect
  },

  data() {
    return {
      // アラート関連
      alertMessage: '',
      alertClass: 'alert-info',

      // ページタイトル関連
      fromParentTitle: 'お知らせ一覧',
      fromParentBtn: 'データ登録',

      // データオブジェクト
      information_obj: [], // お知らせ一覧データ
      information_category_type_obj: [], // お知らせ種別マスタ
      information_public_type_obj: [], // 公開種別マスタ
      information_target_type_obj: [], // 配信先マスタ

      // 検索パラメータ
      sprm: { ...INITIAL_SEARCH_PARAMS },

      // 公開状態選択肢
      status_list: STATUS_OPTIONS,

      // ページネーション
      page: 0,
      page_limit: 10,
      page_order: 'mfj_information_data.id',
      page_sort: 'DESC',

      // ローディング状態
      isLoading: false
    }
  },

  computed: {
    ...mapState({}),

    // 各種マスタデータのゲッター
    information_category_type_list() {
      return this.information_category_type_obj
    },
    information_public_type_list() {
      return this.information_public_type_obj
    },
    information_target_type_list() {
      return this.information_target_type_obj
    },

    // ソート済みお知らせ一覧
    information_list() {
      return [...this.information_obj].sort((a, b) =>
        parseInt(a.sort) < parseInt(b.sort) ? -1 : 1
      )
    }
  },

  async created() {
    await this.loadConfigObj('information_type')
    await this.loadConfigObj('information_category_type')
    await this.loadConfigObj('information_public_type')
    await this.loadConfigObj('information_target_type')
    await this.loadSearchParam()
    await this.initialLoad() // searchInformationの代わりにinitialLoadを呼び出し
  },

  mounted() {
    this.alertMessage = 'お知らせデータ一覧です。'
  },

  methods: {
    /**
     * 初期データ読み込み
     */
    async initializeData() {
      await Promise.all([
        this.loadConfigObj('information_category_type'),
        this.loadConfigObj('information_public_type'),
        this.loadConfigObj('information_target_type')
      ])
      await this.searchInformation()
    },

    /**
     * マスタデータ読み込み
     * @param {string} param - 設定種別
     */
    async loadConfigObj(param) {
      console.log('loadConfigObj param', param)
      try {
        const response = await this.basicPosts_S(
          `${config.BASE_API_URL}/config/${param}`,
          {}
        )
        this[`${param}_obj`] = response.data?.body || []
      } catch (err) {
        console.error(`Error loading ${param}:`, err)
        this[`${param}_obj`] = []
      } finally {
        this.$forceUpdate()
      }
    },

    /**
     * データ切り替え処理
     * @param {number} target - 切り替え対象ID
     */
    async delData(target) {
      if (!confirm('切り替えしますか？')) return

      try {
        const data = this.information_obj.find(event => event.id === target)
        if (data) {
          // 状態を切り替え
          data.stat = data.stat == 1 ? 9 : 1 // 1なら9に、9なら1に切り替え

          await this.$store.dispatch('updateBasic', {
            data,
            target: 'information'
          })
          this.showAlert('状態を切り替えしました', 'alert-success')
        } else {
          this.showAlert('データが見つかりませんでした', 'alert-danger')
        }
      } catch (err) {
        console.error('Delete error:', err)
        this.showAlert('状態を切り替えできませんでした', 'alert-danger')
      } finally {
        await this.loadInformationList()
      }
    },

    /**
     * 検索実行
     */
    async searchInformation() {
      this.isLoading = true
      try {
        await new Promise(resolve => setTimeout(resolve, 500))
        this.page = 0
        await this.loadInformationList()
      } finally {
        this.isLoading = false
      }
    },

    /**
     * お知らせ一覧データ読み込み
     */
    async loadInformationList() {
      const params = this.buildSearchParams()

      try {
        const response = await this.basicPosts_S(
          `${config.BASE_API_URL}/information/lists`,
          params
        )
        this.information_obj = response.data?.body || []
      } catch (err) {
        console.error('Load information error:', err)
        this.information_obj = []
      } finally {
        this.$forceUpdate()
      }
    },

    /**
     * 検索パラメータの構築
     * @returns {Object} 検索パラメータ
     */
    buildSearchParams() {
      const params = {}
      const { stat, subject, category_type, public_type, target_type } =
        this.sprm
      console.log('stat', stat)
      console.log('subject', subject)
      console.log('category_type', category_type)
      console.log('public_type', public_type)
      console.log('target_type', target_type)

      console.log('sprm', this.sprm)
      if (stat !== '') params.stat = [stat]
      if (subject) params.subject = subject
      if (category_type) params.category_type = category_type
      if (public_type) params.public_type = public_type
      if (target_type) params.target_type = target_type
      console.log('params', params)

      return params
    },

    /**
     * 検索条件クリア
     */
    clear() {
      this.sprm = { ...INITIAL_SEARCH_PARAMS }
      this.searchInformation()
    },

    /**
     * 詳細画面への遷移
     * @param {number} target - 対象ID
     */
    handleClick(target) {
      this.$router.push(`/information/${target}/`)
    },

    /**
     * アラートメッセージの表示
     * @param {string} message - メッセージ
     * @param {string} className - アラートクラス名
     */
    showAlert(message, className) {
      this.alertMessage = message
      this.alertClass = className
    },

    /**
     * 日付のフォーマット
     * @param {string} date - 日付文字列
     * @returns {string} フォーマットされた日付
     */
    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('ja-JP')
    },

    /**
     * お知らせ種別のラベル取得
     * @param {number} categoryType - カテゴリータイプID
     * @returns {string} カテゴリーラベル
     */
    getCategoryLabel(categoryType) {
      const category = this.information_category_type_list.find(
        cat => cat.key === categoryType
      )
      return category?.label || '-'
    },

    /**
     * 配信先のラベル取得
     * @param {number} targetType - 配信先タイプID
     * @returns {string} 配信先ラベル
     */
    getTargetLabel(targetType) {
      console.log('targetType', targetType)
      // const target = this.information_target_type_list.find(
      //   t => t.key === targetType
      // )
      // console.log('target', target)
      return targetType == 1 ? '公開' : '非公開'
    },

    /**
     * 公開状態のラベル取得
     * @param {number} stat - 状態値
     * @returns {string} 状態ラベル
     */
    getStatusLabel(stat) {
      const status = this.status_list.find(s => s.key === stat.key)
      return status?.label || '-'
    },

    /**
     * 公開状態に応じたクラス名を取得
     * @param {number} stat - 状態値
     * @returns {string} クラス名
     */
    getStatusClass(stat) {
      return stat === 1 ? 'badge bg-success' : 'badge bg-secondary'
    },

    async initialLoad() {
      this.page = 0
      await this.loadInformationList()
    },

    async loadSearchParam() {
      // Implementation of loadSearchParam method
    }
  }
}
</script>
<style scoped>
.search-section {
  padding: 1.5rem;
  border-radius: 0.5rem;
}

.table th {
  background-color: #f8f9fa;
}

.table .badge {
  font-size: 0.875rem;
}
</style>
